import { Header } from "@ui/Header";
import { ToastContainer } from "@ui/molecules/Toasts/Toasts";
import { useIsMobile } from "@utils/responsive";
import type { JSX, ParentComponent } from "solid-js";
import { onMount, Show } from "solid-js";
import { BoardsProvider } from "~/modules/shared/boards/boardsContext";
import "@ui/layouts/SideMenu.css";
import { touchAccountsReferrer } from "@ui/layouts/AccountLayout/accountsReferrer";
import { CostDropdown } from "@ui/organisms/CostDropdown/CostDropdown";
import { ClampedHeaderTitle } from "~/modules/shared/chat/components/ChatLayout/ClampedHeaderTitle";
import { LayoutProvider } from "../layoutProvider";
import { AskConsumerSidebar } from "./components/AskConsumerSidebar";
import { ConsumerMobileFrameHeader } from "./components/ConsumerMobileFrameHeader";
import { SignedOutHeader } from "./components/SignedOutHeader";
import { pageVars } from "./pageVars";
import "~/modules/shared/chat/components/ChatLayout/ChatLayoutHeader.css";
import "../../../../askgvt/askgvt.css";
import "./AskConsumerLayout.css";

type Props = {
	/** Omit when the page renders its own {@link Header} (`showHeader={false}`). */
	title?: string;
	backLink?: string | -1;
	titleSize?: "small" | "large";
	hasHeaderMargin?: boolean;
	/** Default true when `title` is set; set false for answers/boards-style pages. */
	showHeader?: boolean;
	/** Rendered in the signed-in {@link Header}'s actions; guests and app-embedded pages never see it. */
	headerActions?: JSX.Element;
	/** When set, the usage control shows per-question chat usage instead of account totals. */
	questionId?: string;
	/**
	 * Guest counterpart of `headerActions`, rendered in the guest marketing
	 * header before "Are you a creator?". Only shown with `signedOutTopNav`.
	 */
	guestHeaderActions?: JSX.Element;
	/** Right-aligned in the signed-in mobile top bar; app-embedded pages never see it. */
	mobileFrameActions?: JSX.Element;
	/**
	 * Public creator surfaces (profile, thread, public board): guests get the
	 * full-width sticky marketing header and no sidebar.
	 */
	signedOutTopNav?: boolean;
	/** Condenses the `signedOutTopNav` header on mobile; see {@link SignedOutHeader}. */
	condenseGuestTopNavOnMobile?: boolean;
	/** Defaults to true. Only affects the guest header. */
	showSignUp?: boolean;
};

export const AskConsumerLayout: ParentComponent<Props> = (props) => {
	const vars = pageVars();
	const isMobile = useIsMobile();
	const showSignedOutHeader = () => !vars.isSignedIn && !vars.appEmbedded;
	const guestTopNav = () =>
		Boolean(props.signedOutTopNav && showSignedOutHeader());
	const showSidebar = () => !guestTopNav();
	const showSignedInHeader = () =>
		vars.isSignedIn &&
		props.showHeader !== false &&
		props.title !== undefined &&
		!vars.appEmbedded;

	onMount(() => {
		if (vars.appEmbedded) {
			document.body.classList.add("app-embedded");
		} else if (vars.isSignedIn) {
			touchAccountsReferrer("askgvt");
		}
	});
	const hasHeaderMargin = () => props.hasHeaderMargin ?? true;
	const headerTitle = () => {
		const title = props.title ?? "";
		if (props.questionId && title) {
			return <ClampedHeaderTitle text={title} />;
		}
		return title;
	};

	return (
		<LayoutProvider app="askgvt">
			<BoardsProvider>
				<div
					class="askgvt1"
					classList={{
						"mobile-frame": isMobile() && showSidebar(),
						"askgvt1--guest-top-nav": guestTopNav(),
					}}
				>
					<Show when={isMobile() && showSidebar()}>
						<ConsumerMobileFrameHeader actions={props.mobileFrameActions} />
					</Show>
					<Show when={!isMobile() && showSidebar()}>
						<AskConsumerSidebar />
					</Show>
					<Show when={guestTopNav()}>
						<SignedOutHeader
							variant="marketing"
							showSignUp={props.showSignUp}
							backLink={
								typeof props.backLink === "string" ? props.backLink : undefined
							}
							actions={props.guestHeaderActions}
							condensed={props.condenseGuestTopNavOnMobile && isMobile()}
						/>
					</Show>
					<main>
						<Show when={showSignedOutHeader() && !guestTopNav()}>
							<SignedOutHeader
								variant="compact"
								showSignUp={props.showSignUp}
								backLink={
									typeof props.backLink === "string"
										? props.backLink
										: undefined
								}
							/>
						</Show>
						<Show when={showSignedInHeader()}>
							<Header
								class={props.questionId ? "chat-layout-header" : undefined}
								title={headerTitle()}
								backLink={props.backLink}
								titleSize={props.titleSize}
								style={{ "margin-bottom": hasHeaderMargin() ? "1.5rem" : "0" }}
							>
								{props.headerActions}
								<CostDropdown variant="askgvt" questionId={props.questionId} />
							</Header>
						</Show>
						{props.children}
					</main>
				</div>
				<ToastContainer />
			</BoardsProvider>
		</LayoutProvider>
	);
};
